import React, { useState } from 'react';
import './CustomCalendar.css';

const CustomCalendar = ({ selectedDate, onDateSelect, minDate, onClose }) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = selectedDate ? new Date(`${selectedDate}T00:00:00`) : new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  });

  const [selected, setSelected] = useState(() => {
    if (selectedDate) {
      const date = new Date(`${selectedDate}T00:00:00`);
      return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }
    return null;
  });

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const startOfDay = (date) => {
    const copy = new Date(date);
    copy.setHours(0, 0, 0, 0);
    return copy;
  };

  const minimumDate = () => {
    if (minDate) {
      return startOfDay(new Date(`${minDate}T00:00:00`));
    }
    return startOfDay(new Date());
  };

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleDateClick = (day) => {
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    if (clickedDate < minimumDate()) {
      return;
    }

    setSelected(clickedDate);
    onDateSelect(formatLocalDate(clickedDate));
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const isDateDisabled = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return date < minimumDate();
  };

  const isDateSelected = (day) => {
    if (!selected) return false;
    return (
      selected.getDate() === day &&
      selected.getMonth() === currentMonth.getMonth() &&
      selected.getFullYear() === currentMonth.getFullYear()
    );
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      today.getDate() === day &&
      today.getMonth() === currentMonth.getMonth() &&
      today.getFullYear() === currentMonth.getFullYear()
    );
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dayOfWeek = (firstDay + day - 1) % 7;
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const disabled = isDateDisabled(day);
      const selectedDay = isDateSelected(day);
      const today = isToday(day);

      days.push(
        <button
          type="button"
          key={day}
          className={`calendar-day ${isWeekend ? 'weekend' : ''} ${disabled ? 'disabled' : ''} ${selectedDay ? 'selected' : ''} ${today ? 'today' : ''}`}
          onClick={() => !disabled && handleDateClick(day)}
          disabled={disabled}
          aria-label={formatLocalDate(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day))}
          aria-pressed={selectedDay}
        >
          <span className="day-number">{day}</span>
        </button>
      );
    }

    return days;
  };

  return (
    <div className="custom-calendar">
      {onClose && (
        <button type="button" className="calendar-close-btn" onClick={onClose} aria-label="Close calendar">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      )}

      <div className="calendar-header">
        <h3 className="calendar-month-year">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <div className="calendar-nav-group">
          <button type="button" className="calendar-nav-btn" onClick={handlePrevMonth} aria-label="Previous month">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button type="button" className="calendar-nav-btn" onClick={handleNextMonth} aria-label="Next month">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="calendar-weekdays">
        {dayNames.map((day, index) => (
          <div key={day} className={`weekday ${index === 0 || index === 6 ? 'weekend' : ''}`}>
            {day}
          </div>
        ))}
      </div>

      <div className="calendar-days">
        {renderCalendar()}
      </div>
    </div>
  );
};

export default CustomCalendar;
